/* iter 241 Lot 4 — Engine-aware platform activation panel.
 *
 * Sits at the top of ConnectStep. Uses the new v2 endpoints so a
 * secondary engine never inherits the primary engine's active
 * platforms.
 *
 * 5 canonical statuses displayed :
 *   not_connected              → "Non connecté"
 *   connected_not_enabled_for_engine → "Compte connecté au projet, non activé pour ce moteur" + [Activer pour ce moteur]
 *   enabled_for_engine         → "Activé pour ce moteur" + [Désactiver pour ce moteur]
 *   connection_expired         → "Connexion expirée"
 *   manual_mode                → "Mode manuel"
 */
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { useActiveEngine } from '@/context/EngineContext';
import { Loader2, PowerOff, Power, AlertTriangle, ShieldCheck, XCircle } from 'lucide-react';

const LABELS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  x: 'X (Twitter)',
  pinterest: 'Pinterest',
  brevo: 'Brevo (Email)',
};

export default function EnginePlatformsPanel({ projectId }) {
  const { activeEngine, activeEngineId, multiEnabled } = useActiveEngine();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState('');

  const refresh = useCallback(async () => {
    if (!multiEnabled || !activeEngineId) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(
        `/v2/projects/${projectId}/engines/${activeEngineId}/platforms`
      );
      setPlatforms(data.platforms || []);
    } catch (e) {
      // 501 → legacy fallback
      if (e?.response?.status === 501) setPlatforms([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, activeEngineId, multiEnabled]);

  useEffect(() => { refresh(); }, [refresh]);

  const doEnable = async (platform) => {
    setMutating(platform);
    try {
      await apiClient.post(
        `/v2/projects/${projectId}/engines/${activeEngineId}/platforms/${platform}/enable`,
        { mode: 'auto' }
      );
      toast.success(`${LABELS[platform] || platform} activé pour ce moteur`);
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === 'project_account_not_connected') {
        toast.error("Connecte d'abord le compte au projet avant de l'activer pour ce moteur");
      } else {
        toast.error("Activation impossible");
      }
    } finally {
      setMutating('');
    }
  };

  const doDisable = async (platform) => {
    setMutating(platform);
    try {
      await apiClient.post(
        `/v2/projects/${projectId}/engines/${activeEngineId}/platforms/${platform}/disable`
      );
      toast.success(`${LABELS[platform] || platform} désactivé pour ce moteur`);
      await refresh();
    } catch {
      toast.error("Désactivation impossible");
    } finally {
      setMutating('');
    }
  };

  if (!multiEnabled || !activeEngine) return null;

  return (
    <div
      data-testid="engine-platforms-panel"
      data-engine-id={activeEngineId}
      className="mb-6 p-4 rounded-xl"
      style={{
        background: 'hsl(var(--color-surface-elevated))',
        border: '1px solid hsl(var(--color-border))',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold">
            Plateformes activées pour ce moteur
          </div>
          <div className="text-xs text-semantic-muted mt-0.5">
            Chaque moteur active ses plateformes indépendamment. Un compte
            connecté au projet n&apos;est pas automatiquement actif pour tous
            les moteurs.
          </div>
        </div>
        <button
          data-testid="engine-platforms-refresh"
          onClick={refresh}
          className="text-xs px-2 py-1 rounded"
          style={{ background: 'transparent',
                    border: '1px solid hsl(var(--color-border))' }}
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Rafraîchir'}
        </button>
      </div>

      <div className="grid gap-2">
        {platforms.map((p) => {
          const label = LABELS[p.platform] || p.platform;
          const s = p.engine_activation_status;
          let statusText = p.message;
          let bg = 'transparent';
          let border = '1px solid hsl(var(--color-border))';
          let icon = null;
          if (s === 'enabled_for_engine') {
            bg = 'hsl(var(--color-success-bg))';
            border = '1px solid hsl(var(--color-success) / 0.35)';
            icon = <ShieldCheck className="w-4 h-4" style={{ color: 'hsl(var(--color-success-text))' }} />;
          } else if (s === 'connected_not_enabled_for_engine') {
            bg = 'hsl(var(--color-warning-bg))';
            border = '1px solid hsl(var(--color-warning) / 0.35)';
            icon = <AlertTriangle className="w-4 h-4" style={{ color: 'hsl(var(--color-warning-text))' }} />;
          } else if (s === 'not_connected') {
            icon = <XCircle className="w-4 h-4 opacity-50" />;
          } else if (s === 'connection_expired') {
            bg = 'hsl(var(--color-warning-bg))';
            icon = <AlertTriangle className="w-4 h-4" />;
          }
          return (
            <div
              key={p.platform}
              data-testid={`engine-platform-row-${p.platform}`}
              data-status={s}
              data-enabled={p.enabled_for_engine ? 'true' : 'false'}
              className="flex items-center justify-between gap-3 p-3 rounded-lg"
              style={{ background: bg, border }}
            >
              <div className="flex items-center gap-3">
                {icon}
                <div>
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-xs text-semantic-muted">{statusText}</div>
                  {p.connected_account && (
                    <div className="text-[10px] text-semantic-muted mt-0.5">
                      Compte : {p.connected_account}
                    </div>
                  )}
                </div>
              </div>
              <div>
                {s === 'connected_not_enabled_for_engine' && (
                  <button
                    data-testid={`engine-platform-enable-${p.platform}`}
                    onClick={() => doEnable(p.platform)}
                    disabled={mutating === p.platform}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg"
                    style={{ background: 'hsl(var(--color-primary))', color: 'white' }}
                  >
                    {mutating === p.platform ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Power className="w-3 h-3" />
                    )}
                    Activer pour ce moteur
                  </button>
                )}
                {s === 'enabled_for_engine' && (
                  <button
                    data-testid={`engine-platform-disable-${p.platform}`}
                    onClick={() => doDisable(p.platform)}
                    disabled={mutating === p.platform}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg"
                    style={{ background: 'transparent',
                              border: '1px solid hsl(var(--color-border))' }}
                  >
                    {mutating === p.platform ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <PowerOff className="w-3 h-3" />
                    )}
                    Désactiver
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
